import { useQuery } from '@apollo/client'
import { Grid } from '@material-ui/core'
import React, { useContext, useEffect, useMemo, useState } from 'react'

import { GetCustomerAccountBalanceDocument } from '../../../graphql/camel'
import { CartFragment, GetCustomerAddressesDocument } from '../../../graphql/magento'
import useCamelQuery from '../../../hooks/useCamelQuery'
import useLogAndCaptureError from '../../../hooks/useLogAndCaptureError'
import { TokenContext } from '../../../layouts/context'
import { analyticsItemsFromCartItems } from '../../../lib/analytics/cart'
import { setAnalyticsEvent } from '../../../lib/analytics/state'
import { NonEmptyCartContext } from '../../../lib/cart/context'
import {
  setUpdatePaymentDetailsInput,
  updatePaymentDetailsAndSetPlaceOrderTask$,
} from '../../../lib/cart/state/payment'
import { ValidFormValues } from '../../../lib/cart/types'
import { useDispensaryContext } from '../../../lib/dispensaries'
import { usePageUrl } from '../../../lib/pages'
import { logAndCaptureException } from '../../../utils/errorTools'
import Spinner from '../../Spinner'
import PaymentDetails from './PaymentDetails'

// TODO: no longer used as of 2023-05-04. Remove if not restored to service after ~6 months.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const publishAddPaymentInfoEvent = (cart: CartFragment): void => {
  const currency = cart.prices?.grand_total?.currency
  const totalValue = cart.prices?.grand_total?.value
  if (currency == null || totalValue == null) {
    return
  }
  setAnalyticsEvent({
    event: 'add_payment_info',
    currency,
    value: totalValue,
    coupon: cart.applied_coupons?.[0]?.code,
    payment_type: cart.selected_payment_method?.title,
    items: analyticsItemsFromCartItems(cart.items ?? []),
  })
}

const PaymentDetailsContainer: React.FC = () => {
  const token = useContext(TokenContext)
  const customerCart = useContext(NonEmptyCartContext)
  const dispensary = useDispensaryContext()
  const { navigate } = usePageUrl()

  const [error, setError] = useState<Error | undefined>(undefined)
  const [submitting, setSubmitting] = useState(false)

  const {
    data: accountBalanceData,
    error: accountBalanceError,
    loading: accountBalanceLoading,
  } = useCamelQuery(GetCustomerAccountBalanceDocument, {
    // always fetch fresh customer account history
    fetchPolicy: 'cache-and-network',
  })

  useLogAndCaptureError(accountBalanceError)

  const {
    data: customerAddressesData,
    loading: customerAddressesLoading,
    error: customerAddressesError,
  } = useQuery(GetCustomerAddressesDocument, {
    context: { token },
  })

  useLogAndCaptureError(customerAddressesError)

  const onSubmit = async (values: ValidFormValues.PaymentDetails): Promise<void> => {
    if (customerAddressesLoading) {
      logAndCaptureException(new Error('Customer addresses not loaded before onSubmit'))
      return
    }

    setError(undefined)
    setSubmitting(true)

    setUpdatePaymentDetailsInput({
      fieldValues: values,
      customerAddresses: customerAddressesData?.customer?.addresses,
      customerCart,
      dispensarySlug: dispensary?.slug ?? '',
    })
  }

  useEffect(() => {
    const subscription = updatePaymentDetailsAndSetPlaceOrderTask$.subscribe((result) => {
      if (result._tag === 'Left') {
        // log payment, server, and application errors and capture in Sentry
        logAndCaptureException(result.error)
        setError(result.error)
        setSubmitting(false)
      } else {
        const cart = result.data.customerCart
        publishAddPaymentInfoEvent(cart)
        navigate('/checkout/review')
      }
    })
    return () => subscription.unsubscribe()
  }, [navigate])

  const defaultBillingAddress = useMemo(() => {
    const addresses = customerAddressesData?.customer?.addresses
    return addresses ? addresses.find((address) => address?.default_billing) : undefined
  }, [customerAddressesData?.customer?.addresses])

  if (accountBalanceLoading) {
    return (
      <Grid alignItems="center" justify="center" container item xs={12}>
        <Spinner loading />
      </Grid>
    )
  }
  return (
    <PaymentDetails
      accountBalance={accountBalanceData?.getCustomerAccountBalance}
      cart={customerCart}
      defaultBillingAddress={defaultBillingAddress}
      error={error}
      onSubmit={onSubmit}
      submitting={submitting}
    />
  )
}

export default PaymentDetailsContainer
